import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Alert, Tabs, Select, Row, Col, Radio, Modal, message, Checkbox } from 'antd';
import { CheckCircleFilled, ArrowLeftOutlined, MailOutlined, LockOutlined, MobileOutlined } from '@ant-design/icons';
import { AuthService } from '../../services/auth/AuthService';
import {
  setSession,
  setRegistrationStatus,
  setSubscriptionNearingExpiry,
  setSubscriptionGatingEnabled,
  setHasActiveSubscription,
} from '../../utils/auth';
import { ROUTES } from '../../config/routes';
import {
  getLoginUserRole,
  normalizeLoginUserData,
  navigateAfterStudentLogin,
} from '../../utils/studentProfileLoginFlow';
import MasterDataDropdown from './MasterDataDropdown';
import FiltersService from '../../services/FiltersService';
import { DROPDOWN_CODES } from '../../constants';
import StateAutoComplete from './StateAutoComplete';
import CityAutoComplete from './CityAutoComplete';
import OTPVerification from './OTPVerification';
import PasswordInputWithRequirements from './PasswordInputWithRequirements';
import '../../pages/auth/AuthPage.css';

interface AuthFormProps {
  mode: 'login' | 'register';
  onSuccess?: () => void;
  onCancel?: () => void;
  defaultTab?: 'student' | 'employer' | 'mentor' | 'sponsor';
  className?: string;
  showTabs?: boolean;
  /** When set (e.g. modal login on a public page), navigate here after success instead of dashboard. */
  redirectAfterLogin?: string;
  /** When true, skip default redirects after login; caller handles navigation in onSuccess. */
  deferLoginRedirect?: boolean;
}

const tabData = [
  {
    key: 'student',
    label: 'I Am Student',
    roleId: 1,
  },
  {
    key: 'employer',
    label: 'I Am Organization',
    roleId: 2,
  },
  {
    key: 'mentor',
    label: 'I Am Mentor',
    roleId: 3,
  },
  {
    key: 'sponsor',
    label: 'I Am Sponsor',
    roleId: 4,
  },
];

const AuthForm: React.FC<AuthFormProps> = ({
  mode,
  onSuccess,
  onCancel,
  defaultTab = 'student',
  className = '',
  showTabs = true,
  redirectAfterLogin,
  deferLoginRedirect = false,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(localStorage.getItem('activeTab') || defaultTab);
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null);
  const [selectedSponsorCategory, setSelectedSponsorCategory] = useState<string | null>('Individual');
  const [selectedState, setSelectedState] = useState<string | null>(null);

  // OTP verification state
  const [showOTP, setShowOTP] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [registrationData, setRegistrationData] = useState<any>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  // State for dynamic industry options (only for registration)
  const [industryOptions, setIndustryOptions] = useState<{ label: string; value: string }[]>([]);
  const [loadingIndustryOptions, setLoadingIndustryOptions] = useState(false);

  // State for dynamic business options (only for registration)
  const [businessOptions, setBusinessOptions] = useState<{ label: string; value: string }[]>([]);
  const [loadingBusinessOptions, setLoadingBusinessOptions] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState<'contact' | 'otp' | 'reset'>('contact');
  const [forgotPasswordContactType, setForgotPasswordContactType] = useState<'email' | 'phone'>('email');
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordPhone, setForgotPasswordPhone] = useState('');
  const [forgotPasswordOtp, setForgotPasswordOtp] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'fair' | 'strong' | null>(null);

  // Forgot password Resend OTP state
  const [forgotPasswordTimer, setForgotPasswordTimer] = useState(60);
  const [forgotPasswordCanResend, setForgotPasswordCanResend] = useState(false);

  // Update activeTab when defaultTab prop changes
  useEffect(() => {
    if (!localStorage.getItem('activeTab')) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  const loadIndustryOptions = useCallback(async () => {
    try {
      setLoadingIndustryOptions(true);
      console.log('🔍 Loading industry options...');
      const options = await FiltersService.getDropdownItems(DROPDOWN_CODES.OrgType);
      setIndustryOptions(options);
      console.log('✅ Industry options loaded:', options.length);
    } catch (error) {
      console.error('❌ Error loading industry options:', error);
      setIndustryOptions([]);
    } finally {
      setLoadingIndustryOptions(false);
    }
  }, []);

  const loadBusinessOptions = useCallback(async () => {
    try {
      setLoadingBusinessOptions(true);
      console.log('🔍 Loading business options...');
      const options = await FiltersService.getDropdownItems(DROPDOWN_CODES.BusinessType, selectedIndustry || undefined);
      setBusinessOptions(options);
      console.log('✅ Business options loaded:', options.length);
    } catch (error) {
      console.error('❌ Error loading business options:', error);
      setBusinessOptions([]);
    } finally {
      setLoadingBusinessOptions(false);
    }
  }, [selectedIndustry]);

  // Load industry options when component mounts or when employer tab is active (only for registration)
  useEffect(() => {
    if ((mode === 'register' && activeTab === 'employer') || (mode === 'register' && activeTab === 'sponsor')) {
      form.setFieldValue('sponsorCategory', 'Individual');
      loadIndustryOptions();
    }
  }, [activeTab, form, loadIndustryOptions, mode]);

  // Load business options when industry changes (only for registration)
  useEffect(() => {
    if (mode === 'register' && selectedIndustry) {
      loadBusinessOptions();
    } else {
      setBusinessOptions([]);
    }
  }, [loadBusinessOptions, mode, selectedIndustry]);

  // Set default contact type to phone if sponsor (Moved to top level)
  React.useEffect(() => {
    if (activeTab === 'sponsor') {
      setForgotPasswordContactType('phone');
    }
  }, [activeTab]);

  const getWelcomeMessage = () => {
    if (mode === 'login') {
      switch (activeTab) {
        case 'student':
          return {
            title: 'Login as Student',
            subtitle: 'Sign in as a Student'
          };
        case 'employer':
          return {
            title: 'Welcome Organization!',
            subtitle: 'Sign in to post opportunities and connect with talented students!'
          };
        case 'mentor':
          return {
            title: 'Welcome Mentor!',
            subtitle: 'Sign in to guide and support the next generation of professionals!'
          };
        case 'sponsor':
          return {
            title: 'Welcome Sponsor!',
            subtitle: 'Sign in to support and fund aspiring professionals!'
          };
        default:
          return {
            title: 'Welcome to enpropeL',
            subtitle: 'Sign in to continue'
          };
      }
    } else {
      switch (activeTab) {
        case 'student':
          return {
            title: 'Create Your Account',
            subtitle: 'Register to access internships and more features!'
          };
        case 'employer':
          return {
            title: 'Join as an Organization!',
            subtitle: 'Create your account to connect with talented students!'
          };
        case 'mentor':
          return {
            title: 'Join as a Mentor!',
            subtitle: 'Create your account to guide and support aspiring professionals!'
          };
        case 'sponsor':
          return {
            title: 'Join as a Sponsor!',
            subtitle: 'Create your account to support and fund aspiring professionals!'
          };
        default:
          return {
            title: 'Welcome to enpropeL',
            subtitle: 'Create your account to continue'
          };
      }
    }
  };

  const welcomeMessage = getWelcomeMessage();

  const handleFinish = async (values: any) => {
    setLoading(true);
    setError(null);

    try {
      if (mode === 'login') {
        const role = activeTab === 'employer' ? 'ORGANIZATION' : activeTab ? activeTab.toUpperCase() : '';
        const response = await AuthService.login(values.email, values.password, role);

        if (response.status === 'success') {
          const rawData = (response.data ?? {}) as Record<string, unknown>;
          const userData = normalizeLoginUserData(rawData);
          const token = response.token;
          const refreshToken = response.refreshToken;
          const subscriptionData = (rawData.subscription as Record<string, any>) ?? null;
          const featuresData = (rawData.features as Record<string, any>) ?? null;
          const subStartDate = (rawData.subscription_start_date as string) ?? null;
          const subExpiryDate = (rawData.subscription_end_date as string) ?? null;

          if (token) {
            setSession(token, userData, refreshToken, subscriptionData, featuresData, subStartDate, subExpiryDate);
          } else {
            setError('No authentication token received from server');
            return;
          }

          // Role has subscription plans configured whenever the backend includes this
          // field at all (null/absent means the role has no plans, so gating is off).
          const subscriptionGatingEnabled =
            rawData.requires_subscription_selection !== undefined
            && rawData.requires_subscription_selection !== null;
          setSubscriptionGatingEnabled(subscriptionGatingEnabled);
          setRegistrationStatus((rawData.registration_status as string) ?? null);
          setSubscriptionNearingExpiry(rawData.subscription_nearing_expiry === true);

          const requiresSubSelection =
            rawData.requires_subscription_selection === true ||
            (rawData as any).requiresSubscriptionSelection === true;
          // Server already computed whether an active subscription exists — use it as
          // the source of truth so the guard doesn't fall back to "no subscription"
          // just because the login response omits the full subscription object.
          if (subscriptionGatingEnabled) {
            setHasActiveSubscription(!requiresSubSelection);
          }

          if (deferLoginRedirect) {
            onSuccess?.();
            return;
          }

          if (requiresSubSelection) {
            if (rawData.registration_status === 'PAYMENTPENDING') {
              message.warning(
                'Your registration is incomplete — please choose a subscription plan to continue.',
                6
              );
            }
            window.location.href = ROUTES.PUBLIC.CHOOSE_PLAN;
            onSuccess?.();
            return;
          }

          const propRedirect = redirectAfterLogin?.trim();
          const safePropRedirect =
            propRedirect &&
            propRedirect.startsWith('/') &&
            !propRedirect.startsWith('//') &&
            !propRedirect.includes('://');
          if (safePropRedirect) {
            window.location.href = propRedirect;
            onSuccess?.();
            return;
          }

          const rawRedirect = new URLSearchParams(window.location.search).get(
            'redirect'
          );
          const safeRedirect =
            rawRedirect &&
            rawRedirect.startsWith('/') &&
            !rawRedirect.startsWith('//') &&
            !rawRedirect.includes('://');
          if (safeRedirect) {
            window.location.href = rawRedirect;
            onSuccess?.();
            return;
          }

          // Navigate to appropriate dashboard based on user role
          const userRole = getLoginUserRole(userData);
          if (userRole === 'ORGANIZATION') {
            window.location.href = ROUTES.ORGANIZATION.DASHBOARD;
          } else if (userRole === 'STUDENT') {
            navigateAfterStudentLogin(userData);
          } else if (userRole === 'MENTOR') {
            window.location.href = ROUTES.MENTOR.DASHBOARD;
          } else if (userRole === 'SPONSOR') {
            window.location.href = ROUTES.SPONSOR.DASHBOARD;
          } else {
            window.location.href = ROUTES.STUDENT.DASHBOARD;
          }

          onSuccess?.();
        } else {
          const msg = response.message || 'Login failed';
          setError(msg);
          message.error(msg);
        }
      } else {
        // Registration logic - BYPASS OTP UI
        setRegistrationData(values);

        // Extract contact info
        const phone = values.phone || values.phoneNumber || values.authorizedPhoneNumber;
        setPhoneNumber(phone || '');

        // Pass 000000 to verification and registration directly
        console.log('Bypassing OTP verification for registration...');
        handleFinalRegistration(values, '000000', '000000');
      }
    } catch (error: any) {
      if (mode === 'login') {
        const msg = error?.message || 'Incorrect username or password';
        setError(msg);
        message.error(msg);
      } else {
        setError(error?.message || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleIndustryChange = (value: string) => {
    setSelectedIndustry(value);
    form.setFieldValue('orgBusinessType', undefined);
  };

  const handleOTPVerify = async (otp: string, secondaryOtp?: string) => {
    handleFinalRegistration(registrationData, otp, secondaryOtp);
  };

  const handleFinalRegistration = async (data: any, otp: string, secondaryOtp?: string) => {
    setOtpLoading(true);
    setLoading(true); // For initial bypass call
    setError(null);

    try {
      if (!data) throw new Error("Missing registration data");

      // Get contact info
      const email = data.email || data.authorizedWorkEmail || data.authorizedEmail;
      const phone = data.phone || data.phoneNumber || data.authorizedPhoneNumber;

      const role = activeTab === 'employer' ? 'ORGANIZATION' : activeTab ? activeTab.toUpperCase() : 'STUDENT';
      const isSponsor = activeTab === 'sponsor';

      // Verify OTP with REGISTER request type
      let verifyResponse;
      if (isSponsor) {
        verifyResponse = await AuthService.verifyOtp(phone, otp, 'REGISTER', role, true);
      } else {
        verifyResponse = await AuthService.verifyOtp(email, secondaryOtp || '', 'REGISTER', role, false, otp, phone);
      }

      if (verifyResponse.status !== 'success') {
        setError(verifyResponse.message || 'Verification failed');
        throw new Error(verifyResponse.message || 'Verification failed');
      }

      const registrationDataWithOtp = { ...data };

      // Map OTPs to specific fields
      if (isSponsor) {
        registrationDataWithOtp.phoneOtp = otp;
      } else {
        registrationDataWithOtp.phoneOtp = otp;
        if (secondaryOtp) {
          registrationDataWithOtp.emailOtp = secondaryOtp;
        }
      }

      let response;
      if (activeTab === 'student') {
        response = await AuthService.registerStudent(registrationDataWithOtp);
      } else if (activeTab === 'employer') {
        response = await AuthService.registerOrganization(registrationDataWithOtp);
      } else if (activeTab === 'mentor') {
        response = await AuthService.registerMentor(registrationDataWithOtp);
      } else if (activeTab === 'sponsor') {
        response = await AuthService.registerSponsor(registrationDataWithOtp);
      }

      if (response?.status === 'success') {
        setShowOTP(false);
        const subRequired =
          (response as any)?.data?.subscriptionRequired === true ||
          (response as any)?.data?.subscription_selection_required === true;
        if (subRequired && (response as any)?.token) {
          const respData = (response as any).data ?? {};
          const token = (response as any).token;
          const refreshToken = (response as any).refreshToken ?? (response as any).refresh_token;
          const userData = {
            email: respData.email ?? respData.authorizedPersonEmail,
            firstName: respData.firstName ?? respData.authorizedPersonFirstName,
            lastName: respData.lastName ?? respData.authorizedPersonLastName,
            userId: respData.userId,
            userUniqueId: respData.userUniqueId,
            role: respData.role,
          };
          setSession(token, userData, refreshToken);
          window.location.href = ROUTES.PUBLIC.CHOOSE_PLAN;
          return;
        }
        onSuccess?.();
      } else {
        setError(response?.message || 'Registration failed');
      }
    } catch (error: any) {
      setError(error.message || 'Process failed');
      if (showOTP) throw error; // Re-throw to let OTP component handle it if visible
    } finally {
      setOtpLoading(false);
      setLoading(false);
    }
  };

  const handleOTPResend = async () => {
    try {
      const email = registrationData.email || registrationData.authorizedWorkEmail || registrationData.authorizedEmail;
      const phone = registrationData.phone || registrationData.phoneNumber || registrationData.authorizedPhoneNumber;
      const role = activeTab === 'employer' ? 'ORGANIZATION' : activeTab ? activeTab.toUpperCase() : 'STUDENT';
      const isSponsor = activeTab === 'sponsor';

      // Resend OTP
      let response;
      const orgName = registrationData.orgName;

      if (isSponsor) {
        response = await AuthService.resendOtp(phone, 'REGISTER', role, true, undefined, orgName); // Pass true for isPhone
      } else {
        response = await AuthService.resendOtp(email, 'REGISTER', role, false, phone, orgName);
      }

      if (response.status !== 'success') {
        throw new Error(response.message || 'Failed to resend OTP');
      }
    } catch (error: any) {
      throw error;
    }
  };

  // Forgot password timer logic
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (showForgotPassword && forgotPasswordStep === 'otp' && forgotPasswordTimer > 0 && !forgotPasswordCanResend) {
      interval = setInterval(() => {
        setForgotPasswordTimer((prev) => {
          if (prev <= 1) {
            setForgotPasswordCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [showForgotPassword, forgotPasswordStep, forgotPasswordTimer, forgotPasswordCanResend]);

  // Forgot password helper functions
  const checkPasswordStrength = (password: string): 'weak' | 'fair' | 'strong' | null => {
    if (!password) return null;
    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const isLongEnough = password.length >= 8;

    const score = [hasLower, hasUpper, hasNumber, hasSpecial, isLongEnough].filter(Boolean).length;

    if (score <= 2) return 'weak';
    if (score <= 4) return 'fair';
    return 'strong';
  };

  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value);
    setPasswordStrength(checkPasswordStrength(value));
  };

  const resetForgotPasswordState = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep('contact');
    setForgotPasswordContactType(activeTab === 'sponsor' ? 'phone' : 'email');
    setForgotPasswordEmail('');
    setForgotPasswordPhone('');
    setForgotPasswordOtp('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordStrength(null);
    setError(null);
    // Reset timer state
    setForgotPasswordTimer(60);
    setForgotPasswordCanResend(false);
  };

  const handleForgotPasswordSubmit = async () => {
    const isPhone = forgotPasswordContactType === 'phone';
    const contactValue = isPhone ? forgotPasswordPhone : forgotPasswordEmail;

    if (!contactValue) {
      setError(`Please enter your ${isPhone ? 'phone number' : 'email'}`);
      return;
    }

    if (isPhone) {
      // Validate phone
      const phoneRegex = /^[1-9]\d{9}$/;
      if (!phoneRegex.test(contactValue)) {
        setError('Please enter a valid 10-digit phone number');
        return;
      }
    } else {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactValue)) {
        setError('Please enter a valid email address');
        return;
      }
    }

    setForgotPasswordLoading(true);
    setError(null);

    // BYPASS OTP SENDING
    console.log('Bypassing OTP sending for forgot password...');
    setForgotPasswordStep('otp');
    setForgotPasswordTimer(60);
    setForgotPasswordCanResend(false);
    setForgotPasswordOtp('000000'); // Pre-fill with bypass value

    message.success(`Please proceed with password reset.`);
    setForgotPasswordLoading(false);
  };

  const handleResetPassword = async () => {
    // Ensure we use the bypass value if not set
    const finalOtp = forgotPasswordOtp || '000000';

    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setForgotPasswordLoading(true);
    setError(null);

    const isPhone = forgotPasswordContactType === 'phone';
    const contactValue = isPhone ? forgotPasswordPhone : forgotPasswordEmail;

    try {
      const role = activeTab === 'employer' ? 'ORGANIZATION' : activeTab ? activeTab.toUpperCase() : 'STUDENT';
      const response = await AuthService.forgotPassword(
        contactValue,
        finalOtp,
        newPassword,
        role,
        isPhone
      );

      if (response.status === 'success') {
        message.success('Password reset successful! Please login with your new password.');
        resetForgotPasswordState();
      } else {
        setError(response.message || 'Failed to reset password');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to reset password. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const renderForgotPasswordView = () => {
    // Only show tabs if not sponsor (Sponsor is phone only)
    const showContactTypeTabs = activeTab !== 'sponsor';

    return (
      <div className="forgot-password-view">
        <Button
          type="link"
          onClick={resetForgotPasswordState}
          icon={<ArrowLeftOutlined />}
          className="forgot-password-back-link"
          style={{ padding: 0, marginBottom: 16 }}
        >
          Back to Login
        </Button>

        <div className="auth-header">
          <h2 className="auth-title">Forgot Password</h2>
          <p className="auth-subtitle">
            {forgotPasswordStep === 'contact'
              ? 'Enter your details to receive a verification code'
              : 'Set your New Password'}
          </p>
        </div>

        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            style={{ marginBottom: 16, width: '100%' }}
          />
        )}

        {forgotPasswordStep === 'contact' && (
          <>
            {showContactTypeTabs && (
              <Row justify="center">
                <Col xs={24} md={12}>
                  <Tabs
                    activeKey={forgotPasswordContactType}
                    onChange={(key) => setForgotPasswordContactType(key as 'email' | 'phone')}
                    style={{ marginBottom: 24 }}
                    items={[
                      { key: 'email', label: 'Email' },
                      { key: 'phone', label: 'Phone' }
                    ]}
                  />
                </Col>
              </Row>
            )}

            <Row justify="center">
              <Col xs={24} md={12}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500, color: '#374151', display: 'block', marginBottom: 8 }}>
                    {forgotPasswordContactType === 'email' ? 'Email Address' : 'Phone Number'}
                  </label>
                  {forgotPasswordContactType === 'email' ? (
                    <Input
                      prefix={<MailOutlined style={{ color: '#9ca3af' }} />}
                      placeholder="Enter your email"
                      size="large"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      type="email"
                    />
                  ) : (
                    <Input
                      prefix={<MobileOutlined style={{ color: '#9ca3af' }} />}
                      placeholder="Enter your phone number"
                      size="large"
                      value={forgotPasswordPhone}
                      onChange={(e) => setForgotPasswordPhone(e.target.value)}
                    />
                  )}
                </div>
              </Col>
            </Row>
            <Row justify="center">
              <Col xs={24} md={12}>
                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={handleForgotPasswordSubmit}
                  loading={forgotPasswordLoading}
                  className="auth-button"
                >
                  Continue
                </Button>
              </Col>
            </Row>
          </>
        )}

        {forgotPasswordStep === 'otp' && (
          <>
            <Row justify="center">
              <Col xs={24} md={12}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500, color: '#374151', display: 'block', marginBottom: 8 }}>
                    {forgotPasswordContactType === 'email' ? 'Email Address' : 'Phone Number'}
                  </label>
                  <Input
                    prefix={forgotPasswordContactType === 'email' ? <MailOutlined style={{ color: '#9ca3af' }} /> : <MobileOutlined style={{ color: '#9ca3af' }} />}
                    value={forgotPasswordContactType === 'email' ? forgotPasswordEmail : forgotPasswordPhone}
                    disabled
                    size="large"
                    maxLength={forgotPasswordContactType === 'email' ? 100 : 10}
                  />
                </div>
              </Col>
            </Row>

            <Row justify="center">
              <Col xs={24} md={12}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500, color: '#374151', display: 'block', marginBottom: 8 }}>
                    New Password
                  </label>
                  <PasswordInputWithRequirements
                    placeholder="Enter new password"
                    size="large"
                    value={newPassword}
                    onChange={(e) => handleNewPasswordChange(e.target.value)}
                    maxLength={50}
                    autoComplete="new-password"
                  />
                  {passwordStrength && (
                    <div className={`password-strength-indicator password-strength-${passwordStrength}`}>
                      Password strength: {passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}
                    </div>
                  )}
                  {/* Password Requirements */}
                  <div style={{ marginTop: 16, padding: 12, background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                    <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: '#374151' }}>Password must contain:</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {[
                        { check: newPassword.length >= 8, label: 'At least 8 characters long' },
                        { check: /[A-Z]/.test(newPassword), label: 'Contains uppercase letter (A-Z)' },
                        { check: /[a-z]/.test(newPassword), label: 'Contains lowercase letter (a-z)' },
                        { check: /\d/.test(newPassword), label: 'Contains a number (0-9)' },
                        { check: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword), label: 'Contains a special character (!@#$%^&*)' }
                      ].map((req, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                          <span style={{
                            color: newPassword ? (req.check ? '#10b981' : '#ef4444') : '#9ca3af',
                            fontSize: 14,
                            fontWeight: 'bold'
                          }}>
                            {newPassword ? (req.check ? '✓' : '✗') : '○'}
                          </span>
                          <span style={{ color: newPassword ? (req.check ? '#374151' : '#6b7280') : '#6b7280' }}>
                            {req.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <Row justify="center">
              <Col xs={24} md={12}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontWeight: 500, color: '#374151', display: 'block', marginBottom: 8 }}>
                    Confirm New Password
                  </label>
                  <Input.Password
                    prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
                    placeholder="Confirm new password"
                    size="large"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    maxLength={50}
                  />
                </div>
              </Col>
            </Row>

            <Row justify="center">
              <Col xs={24} md={12}>
                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={handleResetPassword}
                  loading={forgotPasswordLoading}
                  className="auth-button"
                  disabled={!forgotPasswordOtp || !newPassword || !confirmNewPassword}
                >
                  Reset Password
                </Button>
              </Col>
            </Row>
          </>
        )}
      </div>
    );
  };

  const renderLoginForm = () => (
    <>
      <Row justify="center">
        <Col xs={24} md={12}>
          <Form.Item
            label="Username"
            name="email"
            rules={[
              { required: true, message: 'Please enter your email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input placeholder="Enter your email" size="large" autoComplete="off" maxLength={100} className="auth-field" />
          </Form.Item>
        </Col>
      </Row>
      <Row justify="center">
        <Col xs={24} md={12}>
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: 'Please enter your password!' }]}
          >
            <Input.Password placeholder="Enter your password" size="large" autoComplete="new-password" maxLength={50} />
          </Form.Item>
          <div style={{ textAlign: 'right', marginTop: -16, marginBottom: 16 }}>
            <Button
              type="link"
              onClick={() => {
                resetForgotPasswordState();
                setShowForgotPassword(true);
              }}
              style={{ padding: 0 }}
            >
              Forgot your password
            </Button>
          </div>
        </Col>
      </Row>
    </>
  );

  const renderConsentCheckboxes = () => (
    <>
      <Form.Item
        name="tosConsent"
        valuePropName="checked"
        rules={[
          {
            validator: (_, value) => value
              ? Promise.resolve()
              : Promise.reject(new Error('Please agree to the Terms of Service to continue.'))
          }
        ]}
      >
        <Checkbox>
          I agree to the{' '}
          <a className="consent-link" href="/terms" target="_blank" rel="noreferrer">
            enpropeL Terms of Service
          </a>{' '}
          and acknowledge that enpropeL acts as a facilitator platform.
        </Checkbox>
      </Form.Item>
      <Form.Item
        name="privacyConsent"
        valuePropName="checked"
        rules={[
          {
            validator: (_, value) => value
              ? Promise.resolve()
              : Promise.reject(new Error('Please agree to the Privacy Policy to continue.'))
          }
        ]}
      >
        <Checkbox>
          I agree to the{' '}
          <a className="consent-link" href="/privacy" target="_blank" rel="noreferrer">
            enpropeL Privacy Policy
          </a>{' '}
          and consent to the collection, storage, processing, and sharing of my personal data for platform
          operations and opportunity matching.
        </Checkbox>
      </Form.Item>
      <Form.Item
        name="transactionalConsent"
        valuePropName="checked"
        rules={[
          {
            validator: (_, value) => value
              ? Promise.resolve()
              : Promise.reject(new Error('Please consent to transactional communications to continue.'))
          }
        ]}
      >
        <Checkbox>
          I consent to receive transactional communications from enpropeL via email, SMS, or inplatform
          notifications related to my account and activities.
        </Checkbox>
      </Form.Item>
      <Form.Item name="promotionalConsent" valuePropName="checked">
        <Checkbox>
          I would like to receive updates, opportunities, and promotional communications from enpropeL.
        </Checkbox>
      </Form.Item>
    </>
  );

  const renderStudentRegistrationForm = () => (
    <>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="First Name"
            name="firstName"
            rules={[{ required: true, message: 'Please enter your first name!' }]}
          >
            <Input placeholder="Enter your first name" size="large" maxLength={50} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Last Name"
            name="lastName"
            rules={[{ required: true, message: 'Please enter your last name!' }]}
          >
            <Input placeholder="Enter your last name" size="large" maxLength={50} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label="Email (Username)"
        name="email"
        rules={[
          { required: true, message: 'Please enter your email!' },
          { type: 'email', message: 'Please enter a valid email!' }
        ]}
      >
        <Input placeholder="Enter your email" size="large" autoComplete="off" maxLength={100} />
      </Form.Item>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: 'Please enter your password!' }]}
          >
            <PasswordInputWithRequirements placeholder="Enter your password" size="large" maxLength={50} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={["password"]}
            rules={[
              { required: true, message: 'Please confirm your password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm your password" size="large" maxLength={50} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Gender"
            name="gender"
            rules={[{ required: true, message: 'Please select your gender!' }]}
          >
            <MasterDataDropdown
              dropdownCode={DROPDOWN_CODES.Gender}
              value={form.getFieldValue('gender')}
              onChange={(value) => form.setFieldValue('gender', value)}
              placeholder="Select gender"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Phone Number"
            name="phoneNumber"
            rules={[
              { required: true, message: 'Please enter your phone number!' },
              { pattern: /^[1-9]\d{9}$/, message: 'Please enter a valid 10-digit phone number' }
            ]}
          >
            <Input
              placeholder="Enter your phone number"
              size="large"
              maxLength={10}
              onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) { e.preventDefault(); } }}
            />
          </Form.Item>
        </Col>
      </Row>

      {renderConsentCheckboxes()}
    </>
  );

  const renderOrganizationRegistrationForm = () => (
    <>
      <Form.Item
        label="Organization Name"
        name="orgName"
        rules={[{ required: true, message: 'Please enter your organization name!' }]}
      >
        <Input placeholder="Enter your organization name" size="large" maxLength={100} />
      </Form.Item>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Organization Type"
            name="orgType"
            rules={[{ required: true, message: 'Please select your industry!' }]}
          >
            <Select
              placeholder="Select your industry"
              size="large"
              options={industryOptions}
              onChange={handleIndustryChange}
              loading={loadingIndustryOptions}
              notFoundContent={loadingIndustryOptions ? "Loading..." : "No options available"}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Business"
            name="orgBusinessType"
            rules={[{ required: true, message: 'Please select your business type!' }]}
          >
            <Select
              placeholder="Select your business type"
              size="large"
              options={businessOptions}
              disabled={!selectedIndustry}
              loading={loadingBusinessOptions}
              notFoundContent={loadingBusinessOptions ? "Loading..." : "No options available"}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label="Authorized Person Work Email (Username)"
        name="authorizedWorkEmail"
        rules={[
          { required: true, message: 'Please enter your Authorized Person Work Email!' },
          { type: 'email', message: 'Please enter a valid email!' }
        ]}
      >
        <Input placeholder="Enter your organization email" size="large" maxLength={100} />
      </Form.Item>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Password"
            name="password"
            rules={[
              { required: true, message: 'Please enter your password!' },
              { min: 8, message: 'Password must be at least 8 characters!' }
            ]}
          >
            <PasswordInputWithRequirements placeholder="Enter your password" size="large" maxLength={50} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={["password"]}
            rules={[
              { required: true, message: 'Please confirm your password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm your password" size="large" maxLength={50} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Authorized Person First Name"
            name="authorizedFName"
            rules={[{ required: true, message: 'Please enter your First Name!' }]}
          >
            <Input placeholder="Enter your First Name" size="large" maxLength={50} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Authorized Person Last Name"
            name="authorizedLName"
            rules={[{ required: true, message: 'Please enter your Last Name!' }]}
          >
            <Input placeholder="Enter your Last Name" size="large" maxLength={50} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Authorized Person Phone"
            name="authorizedPhoneNumber"
            rules={[
              { required: true, message: 'Please enter your Phone Number!' },
              { pattern: /^[1-9]\d{9}$/, message: 'Please enter a valid 10-digit phone number' }
            ]}
          >
            <Input placeholder="Enter your Phone Number" size="large" maxLength={10} onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) { e.preventDefault(); } }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Authorized Person Department"
            name="authorizedPersonDepartment"
            rules={[{ required: true, message: 'Please enter your Department!' }]}
          >
            <Input placeholder="Enter your Department" size="large" maxLength={100} />
          </Form.Item>
        </Col>
      </Row>

      {renderConsentCheckboxes()}
    </>
  );

  const renderMentorRegistrationForm = () => (
    <>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="First Name"
            name="firstName"
            rules={[{ required: true, message: 'Please enter your first name!' }]}
          >
            <Input placeholder="Enter your first name" size="large" maxLength={50} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Last Name"
            name="lastName"
            rules={[{ required: true, message: 'Please enter your last name!' }]}
          >
            <Input placeholder="Enter your last name" size="large" maxLength={50} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label="Email (Username)"
        name="email"
        rules={[
          { required: true, message: 'Please enter your email!' },
          { type: 'email', message: 'Please enter a valid email!' }
        ]}
      >
        <Input placeholder="Enter your email" size="large" maxLength={100} />
      </Form.Item>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: 'Please enter your password!' }]}
          >
            <PasswordInputWithRequirements placeholder="Enter your password" size="large" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={["password"]}
            rules={[
              { required: true, message: 'Please confirm your password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm your password" size="large" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Gender"
            name="gender"
            rules={[{ required: true, message: 'Please select your gender!' }]}
          >
            <MasterDataDropdown
              dropdownCode={DROPDOWN_CODES.Gender}
              value={form.getFieldValue('gender')}
              onChange={(value) => form.setFieldValue('gender', value)}
              placeholder="Select gender"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Phone Number"
            name="phoneNumber"
            rules={[
              { required: true, message: 'Please enter your phone number!' },
              { pattern: /^[1-9]\d{9}$/, message: 'Please enter a valid 10-digit phone number' }
            ]}
          >
            <Input placeholder="Enter your phone number" size="large" maxLength={10} />
          </Form.Item>
        </Col>
      </Row>

      {renderConsentCheckboxes()}
    </>
  );

  const renderSponsorRegistrationForm = () => (
    <>
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px', color: 'rgba(0, 0, 0, 0.85)' }}>Sponsor Category</div>
        <Form.Item
          name="sponsorCategory"
          rules={[{ required: true, message: 'Please enter your sponsor category!' }]}
        >
          <Radio.Group
            onChange={(e) => setSelectedSponsorCategory(e.target.value)}
            value={selectedSponsorCategory}
            size="large"
            style={{ fontSize: '18px' }}
          >
            <Radio value="Individual" style={{ fontSize: '18px', marginRight: '32px' }}>Individual</Radio>
            <Radio value="Organization" style={{ fontSize: '18px' }}>Organization</Radio>
          </Radio.Group>
        </Form.Item>
      </div>

      {selectedSponsorCategory === 'Organization' && <>

        <Form.Item
          label="Organization Name"
          name="orgName"
          rules={[{ required: true, message: 'Please enter your organization name!' }]}
        >
          <Input placeholder="Enter your organization name" size="large" />
        </Form.Item>

        <Form.Item
          label="Website"
          name="website"
          rules={[{ required: true, message: 'Please enter your website!' }, { type: 'url', message: 'Please enter a valid website!' }]}
        >
          <Input placeholder="Enter your website" size="large" maxLength={100} />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Organization Type"
              name="orgType"
              rules={[{ required: true, message: 'Please select your industry!' }]}
            >
              <Select
                placeholder="Select your industry"
                size="large"
                options={industryOptions}
                onChange={handleIndustryChange}
                loading={loadingIndustryOptions}
                notFoundContent={loadingIndustryOptions ? "Loading..." : "No options available"}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Business"
              name="orgBusinessType"
              rules={[{ required: true, message: 'Please select your business type!' }]}
            >
              <Select
                placeholder="Select your business type"
                size="large"
                options={businessOptions}
                disabled={!selectedIndustry}
                loading={loadingBusinessOptions}
                notFoundContent={loadingBusinessOptions ? "Loading..." : "No options available"}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Authorized Person First Name"
              name="authorizedFName"
              rules={[{ required: true, message: 'Please enter your First Name!' }]}
            >
              <Input placeholder="Enter your First Name" size="large" maxLength={50} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Authorized Person Last Name"
              name="authorizedLName"
              rules={[{ required: true, message: 'Please enter your Last Name!' }]}
            >
              <Input placeholder="Enter your Last Name" size="large" maxLength={50} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Authorized Person Work Email (Username)"
              name="authorizedEmail"
              rules={[
                { required: true, message: 'Please enter your Authorized Person Work Email!' },
                { type: 'email', message: 'Please enter a valid email!' }
              ]}
            >
              <Input placeholder="Enter your organization email" size="large" autoComplete="off" maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Authorized Person Phone"
              name="authorizedPhoneNumber"
              rules={[
                { required: true, message: 'Please enter your phone number!' },
                { pattern: /^[1-9]\d{9}$/, message: 'Please enter a valid 10-digit phone number' }
              ]}
            >
              <Input
                placeholder="Enter your Phone Number"
                size="large"
                maxLength={10}
                onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) { e.preventDefault(); } }}
              />
            </Form.Item>
          </Col>
        </Row>


      </>

      }

      {
        selectedSponsorCategory === 'Individual' && <>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="First Name"
                name="firstName"
                rules={[{ required: true, message: 'Please enter your first name!' }]}
              >
                <Input placeholder="Enter your first name" size="large" maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Last Name"
                name="lastName"
                rules={[{ required: true, message: 'Please enter your last name!' }]}
              >
                <Input placeholder="Enter your last name" size="large" maxLength={50} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={24}>
              <Form.Item
                label="Email (Username)"
                name="email"
                rules={[
                  { required: true, message: 'Please enter your Email!' },
                  { type: 'email', message: 'Please enter a valid email!' }
                ]}
              >
                <Input placeholder="Enter your email" size="large" autoComplete="off" maxLength={100} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Gender"
                name="gender"
                rules={[{ required: true, message: 'Please select your gender!' }]}
              >
                <MasterDataDropdown
                  dropdownCode={DROPDOWN_CODES.Gender}
                  value={form.getFieldValue('gender')}
                  onChange={(value) => form.setFieldValue('gender', value)}
                  placeholder="Select gender"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Phone Number"
                name="phoneNumber"
                rules={[
                  { required: true, message: 'Please enter your phone number!' },
                  { pattern: /^[1-9]\d{9}$/, message: 'Please enter a valid 10-digit phone number' }
                ]}
              >
                <Input
                  placeholder="Enter your phone number"
                  size="large"
                  maxLength={10}
                  onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) { e.preventDefault(); } }}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      }

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Password"
            name="password"
            rules={[
              { required: true, message: 'Please enter your password!' },
              { min: 8, message: 'Password must be at least 8 characters!' }
            ]}
          >
            <PasswordInputWithRequirements placeholder="Enter your password" size="large" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={["password"]}
            rules={[
              { required: true, message: 'Please confirm your password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm your password" size="large" maxLength={50} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={selectedSponsorCategory === 'Organization' ? 24 : 12}>
          <Form.Item
            label="Address Line 1"
            name="addressLine1"
            rules={[
              { required: true, message: 'Please enter your address!' }
            ]}
          >
            <Input placeholder="Enter your address" size="large" maxLength={150} className="auth-field" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={selectedSponsorCategory === 'Organization' ? 12 : 12}>
          <Form.Item
            label="Address Line 2"
            name="addressLine2"
          >
            <Input placeholder="Enter your address" size="large" maxLength={150} className="auth-field" />
          </Form.Item>
        </Col>
        {selectedSponsorCategory === 'Organization' && (
          <Col xs={24} sm={12}>
            <Form.Item
              label="Phone Number"
              name="phoneNumber"
              rules={[
                { required: true, message: 'Please enter your phone number!' },
                { pattern: /^[1-9]\d{9}$/, message: 'Please enter a valid 10-digit phone number' }
              ]}
            >
              <Input
                placeholder="Enter your phone number"
                size="large"
                maxLength={10}
                onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) { e.preventDefault(); } }}
              />
            </Form.Item>
          </Col>
        )}
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="State"
            name="state"
            rules={[{ required: true, message: 'Please select your state!' }]}
          >
            <StateAutoComplete
              value={form.getFieldValue('state')}
              onChange={(value: string, option?: { label: string, value: string }) => {
                form.setFieldValue('state', value);
                setSelectedState(value);
                form.setFieldValue('cityCode', ''); // Clear city when state changes
              }}
              placeholder="Select State"
              size="large"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="City"
            name="cityCode"
            rules={[{ required: true, message: 'Please select your city!' }]}
          >
            <CityAutoComplete
              value={form.getFieldValue('cityCode')}
              onChange={(value: string, option?: { label: string, value: string }) => {
                form.setFieldValue('cityCode', value);
              }}
              stateCode={form.getFieldValue('state')}
              placeholder="Select City"
              disabled={!selectedState}
              size="large"
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Postal Code"
            name="postalCode"
            rules={[
              { required: true, message: 'Please enter your postal code!' },
              { pattern: /^\d{6}$/, message: 'Please enter a valid 6-digit postal code!' }
            ]}
          >
            <Input
              placeholder="Enter your postal code"
              size="large"
              maxLength={6}
              onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) { e.preventDefault(); } }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Address Type"
            name="addressTypeCode"
            rules={[{ required: true, message: 'Please select your address type!' }]}
          >
            <MasterDataDropdown
              dropdownCode={DROPDOWN_CODES.AddressType}
              value={form.getFieldValue('addressTypeCode')}
              onChange={(value) => form.setFieldValue('addressTypeCode', value)}
              placeholder="Select Address Type"
            />
          </Form.Item>
        </Col>
      </Row>

      {renderConsentCheckboxes()}
    </>
  );

  return (
    <>
      <div className={className}>
        {showTabs && (
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key as 'student' | 'employer' | 'mentor' | 'sponsor');
              localStorage.setItem('activeTab', key);

              // Clear form and state on tab switch
              form.resetFields();
              setError(null);
              setSelectedIndustry(null);
              setSelectedSponsorCategory('Individual');
              form.setFieldsValue({ sponsorCategory: 'Individual' });
              setSelectedState(null);
              setRegistrationData(null);
              setPhoneNumber('');
            }}
            items={tabData.map(tab => ({
              key: tab.key,
              label: (
                <div className="tab-label">
                  {tab.label}
                  {activeTab === tab.key && (
                    <CheckCircleFilled className="tab-check-icon" />
                  )}
                </div>
              ),
              disabled: showForgotPassword
            }))}
            className="auth-tabs"
            style={{ width: '100%' }}
            tabBarStyle={{ width: '100%' }}
          />
        )}

        {showForgotPassword && mode === 'login' ? (
          renderForgotPasswordView()
        ) : (
          <>
            <div className="auth-header">
              <h2 className="auth-title">{welcomeMessage.title}</h2>
              <p className="auth-subtitle">{welcomeMessage.subtitle}</p>
            </div>

            {error && (
              <Alert
                type="error"
                message={error}
                showIcon
                style={{ marginBottom: 16, width: '100%' }}
              />
            )}

            <Form
              form={form}
              name={`${mode}Form`}
              layout="vertical"
              onFinish={handleFinish}
              className="auth-form"
              autoComplete="off"
            >
              {mode === 'login' ? (
                renderLoginForm()
              ) : (
                activeTab === 'student' ?
                  renderStudentRegistrationForm() : activeTab === 'employer' ?
                    renderOrganizationRegistrationForm() : activeTab === 'mentor' ?
                      renderMentorRegistrationForm() : activeTab === 'sponsor' ?
                        renderSponsorRegistrationForm() : null
              )}
              <Row justify="center">
                <Col xs={24} md={12}>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={loading}
                      block
                      size="large"
                      className="auth-button"
                    >
                      {mode === 'login' ? 'Login' : 'Create Account'}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </>
        )}
      </div>

      {/* OTP Verification Modal */}
      <Modal
        open={showOTP}
        onCancel={() => {
          setShowOTP(false);
          setRegistrationData(null);
          setPhoneNumber('');
          setError(null);
        }}
        footer={null}
        closable={true}
        centered
        width={600}
        destroyOnClose
        maskClosable={false}
        maskStyle={{ backdropFilter: 'blur(10px)' }}
      >
        <OTPVerification
          phone={phoneNumber}
          email={registrationData?.email || registrationData?.authorizedWorkEmail || registrationData?.authorizedEmail}
          isDualVerification={activeTab !== 'sponsor'}
          onVerify={handleOTPVerify}
          onResend={handleOTPResend}
          loading={otpLoading}
          onCancel={() => {
            setShowOTP(false);
            setRegistrationData(null);
            setPhoneNumber('');
            setError(null);
          }}
        />
      </Modal>
    </>
  );
};

export default AuthForm;
